import React, { useMemo } from "react";
import {
    View,
    Text,
    StyleSheet,
    TouchableOpacity,
    Linking,
    Dimensions,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useNavigation } from "@react-navigation/native";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { useTheme } from "../contexts/ThemeContext";
import { RootStackParamList } from "../navigation";
import CrawlableLink from "./CrawlableLink";

const { width } = Dimensions.get("window");
const isSmallMobile = width < 380;

// Site navigation as real anchors. The header renders its nav through
// TouchableOpacity, which react-native-web emits as a <div>, so before this the
// rendered DOM contained no crawlable link to /about or /contact from any page —
// they were reachable only via the sitemap.
const FOOTER_LINKS: Array<{
  label: string;
  path: string;
  screen: keyof RootStackParamList;
}> = [
    { label: "Home", path: "/", screen: "Home" },
    { label: "About", path: "/about", screen: "About" },
    { label: "Contact", path: "/contact", screen: "Contact" },
    { label: "Privacy Policy", path: "/privacy-policy", screen: "PrivacyPolicy" },
];

const Footer: React.FC = () => {
    const { theme } = useTheme();
    const navigation =
        useNavigation<NativeStackNavigationProp<RootStackParamList>>();

    const themed = useMemo(
        () => ({
            footerBg: theme === "dark" ? "#18181B" : "#F7FAFC",
            footerText: theme === "dark" ? "#A0AEC0" : "#718096",
            footerLogo: theme === "dark" ? "#F2F2F2" : "#272727",
            socialIcon: theme === "dark" ? "#232327" : "#EDF2F7",
            socialIconColor: theme === "dark" ? "#C2C2C2" : "#555555",
            borderTopColor: theme === "dark" ? "#232327" : "#E2E8F0",
        }),
        [theme]
    );

    return (
        <View
            style={[
                styles.footer,
                {
                    backgroundColor: themed.footerBg,
                    borderTopColor: themed.borderTopColor,
                },
            ]}
        >
            <View style={styles.footerLogoContainer}>
                <Ionicons
                    name="flask"
                    size={isSmallMobile ? 24 : 28}
                    color={themed.footerLogo}
                />

                <Text style={[styles.footerLogoText, { color: themed.footerLogo }]}>
                    BC STEM
                </Text>
            </View>
            <View
                accessibilityRole={"navigation" as any}
                aria-label="Footer"
                style={styles.footerNav}
            >
                {FOOTER_LINKS.map((link) => (
                    <CrawlableLink
                        key={link.path}
                        to={link.path}
                        onPress={() => navigation.navigate(link.screen as any)}
                        style={[styles.footerNavLink, { color: themed.footerText }]}
                    >
                        {link.label}
                    </CrawlableLink>
                ))}
            </View>
            <Text style={[styles.footerText, { color: themed.footerText }]}>
                © 2025 BC STEM Opportunities Database
            </Text>
            <View style={styles.footerSocial}>
                <TouchableOpacity
                    style={[
                        styles.socialIcon,
                        isSmallMobile && styles.socialIconSmall,
                        { backgroundColor: themed.socialIcon },
                    ]}
                    onPress={() => Linking.openURL("https://instagram.com")}
                >
                    <Ionicons
                        name="logo-instagram"
                        size={isSmallMobile ? 18 : 22}
                        color={themed.socialIconColor}
                    />
                </TouchableOpacity>
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    footer: {
        padding: isSmallMobile ? 30 : 40,
        alignItems: "center",
        borderTopWidth: 1,
        width: "100%",
    },
    footerLogoContainer: {
        flexDirection: "row",
        alignItems: "center",
        marginBottom: 16,
    },
    footerLogoText: {
        fontSize: 18,
        fontWeight: "700",
        marginLeft: 8,
        fontFamily: "Inter-Bold",
    },
    footerNav: {
        flexDirection: "row",
        flexWrap: "wrap",
        justifyContent: "center",
        gap: isSmallMobile ? 12 : 20,
        marginBottom: 16,
    },
    footerNavLink: {
        fontSize: 14,
        fontFamily: "Inter-Medium",
    },
    footerText: {
        fontSize: 14,
        marginBottom: 16,
        fontFamily: "Inter-Regular",
        textAlign: "center",
    },
    footerSocial: {
        flexDirection: "row",
        gap: 16,
    },
    socialIcon: {
        width: 40,
        height: 40,
        borderRadius: 20,
        alignItems: "center",
        justifyContent: "center",
    },
    socialIconSmall: {
        width: 36,
        height: 36,
        borderRadius: 18,
    },
});

export default Footer;
